import { useState } from 'react';
import { StatusChip } from '../../components/ticket/StatusChip';
import { TypeIcon } from '../../components/ticket/TypeIcon';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import type { TicketListItem } from '../../api/types';

/**
 * A capped list of tickets under a version.
 *
 * 🔴 THE CAP IS NOT COSMETIC. `v1.0.34` really has 138 tickets in the fix-version relation, and
 * rendering all of them inside one card made a 390px screen scroll for a minute past ONE version to
 * reach the next — with five versions on the page. Found by rendering it at a real 390px.
 *
 * ⛔ THE COUNT ABOVE STAYS 138. The cap changes how many rows are DRAWN, never what the page claims:
 * the button says how many are hidden, and it is the reader who decides to open them. A cap that
 * quietly changed the number would be the confident wrong number this view exists to avoid.
 */
const VISIBLE_BY_DEFAULT = 8;

export function TicketMiniList({
  tickets,
  showStatus = true,
  onOpenTicket,
}: {
  tickets: readonly TicketListItem[];
  showStatus?: boolean;
  onOpenTicket: (ticket: TicketListItem) => void;
}) {
  const vocab = useVocabulary();
  const [showAll, setShowAll] = useState(false);

  const visible = showAll ? tickets : tickets.slice(0, VISIBLE_BY_DEFAULT);
  const hidden = tickets.length - visible.length;

  return (
    <>
      <ul className="mt-1.5 space-y-1">
        {visible.map((ticket) => (
          <li key={ticket.id}>
            <button
              type="button"
              onClick={() => onOpenTicket(ticket)}
              className="flex w-full min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-left hover:bg-black/5 dark:hover:bg-white/10"
            >
              <TypeIcon type={vocab.type(ticket.type)} size={13} />
              <span className="shrink-0 font-mono text-[11px] text-[color:var(--color-ink-muted)]">
                {ticket.key}
              </span>
              <span className="min-w-0 flex-1 truncate text-xs">{ticket.title}</span>
              {showStatus ? (
                <StatusChip status={vocab.status(ticket.status)} className="max-w-[8rem]" />
              ) : null}
            </button>
          </li>
        ))}
      </ul>

      {hidden > 0 ? (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-1 rounded border border-[color:var(--color-line)] px-2 py-1 text-xs font-semibold"
        >
          Show {hidden} more
        </button>
      ) : null}

      {showAll && tickets.length > VISIBLE_BY_DEFAULT ? (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="mt-1 rounded border border-[color:var(--color-line)] px-2 py-1 text-xs font-semibold"
        >
          Show fewer
        </button>
      ) : null}
    </>
  );
}
